import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  FileText, Plus, Search, CheckCircle, Clock, AlertCircle, 
  Building2, Shield, Calendar, Download, Send, Edit
} from 'lucide-react';
import api from '../../lib/api';
import SubscriptionGate from '../../components/subscription/SubscriptionGate';

export default function UCCPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('filings'); // 'filings' or 'create'
  const [filings, setFilings] = useState([]);
  const [collateralCategories, setCollateralCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    debtor_name: '',
    debtor_jurisdiction: '',
    debtor_address: '',
    debtor_type: 'organization',
    secured_party_name: '',
    secured_party_address: '',
    secured_party_jurisdiction: '',
    collateral_category: 'equipment',
    collateral_description: '',
    collateral_details: '',
    entity_id: null
  });

  useEffect(() => {
    loadFilings();
    loadCollateralCategories();
  }, []);

  const loadFilings = async () => {
    try {
      const res = await api.get('/ucc/filings');
      setFilings(res.data.filings || []);
    } catch (err) {
      console.error('Failed to load UCC filings:', err);
    }
  };

  const loadCollateralCategories = async () => {
    try {
      const res = await api.get('/ucc/collateral-categories');
      setCollateralCategories(res.data.categories || []);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGenerateUCC = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const res = await api.post('/ucc/generate', {
        debtor: {
          name: formData.debtor_name,
          jurisdiction: formData.debtor_jurisdiction,
          address: formData.debtor_address,
          type: formData.debtor_type
        },
        secured_party: {
          name: formData.secured_party_name,
          address: formData.secured_party_address,
          jurisdiction: formData.secured_party_jurisdiction
        },
        collateral: {
          category: formData.collateral_category,
          description: formData.collateral_description,
          details: formData.collateral_details
        },
        entity_id: formData.entity_id
      });

      setSuccess(`UCC-1 Filing Generated: ${res.data.filing_number}`);
      setActiveTab('filings');
      loadFilings();
      // Reset form
      setFormData({
        debtor_name: '',
        debtor_jurisdiction: '',
        debtor_address: '',
        debtor_type: 'organization',
        secured_party_name: '',
        secured_party_address: '',
        secured_party_jurisdiction: '',
        collateral_category: 'equipment',
        collateral_description: '',
        collateral_details: '',
        entity_id: null
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate UCC filing');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitFiling = async (filingId) => {
    if (!window.confirm('Submit this UCC-1 filing? This action cannot be undone.')) return;
    
    try {
      await api.post(`/ucc/submit/${filingId}`);
      setSuccess('UCC Filing submitted successfully');
      loadFilings();
    } catch (err) {
      setError(err.response?.data?.detail || 'Submission failed');
    }
  };

  const handleContinuation = async (filingId) => {
    if (!window.confirm('File UCC-3 Continuation to extend this filing for 5 more years?')) return;
    
    try {
      const res = await api.post(`/ucc/continue/${filingId}`);
      setSuccess(`UCC-3 Continuation filed! Filing extended until ${new Date(res.data.new_expiry_date).toLocaleDateString()}`);
      loadFilings();
    } catch (err) {
      setError(err.response?.data?.detail || 'Continuation failed');
    }
  };

  const handleTermination = async (filingId) => {
    const reason = window.prompt('Reason for termination (optional):');
    if (reason === null) return; // User cancelled
    
    if (!window.confirm('Terminate this UCC filing? This will release all secured interests.')) return;
    
    try {
      await api.post(`/ucc/terminate/${filingId}`, {
        amendment_type: 'termination',
        reason: reason || 'Debt satisfied'
      });
      setSuccess('UCC-3 Termination filed successfully');
      loadFilings();
    } catch (err) {
      setError(err.response?.data?.detail || 'Termination failed');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-slate-500/20 text-slate-400', icon: Edit, label: 'Draft' },
      ready_for_filing: { color: 'bg-blue-500/20 text-blue-400', icon: Clock, label: 'Ready' },
      filed: { color: 'bg-green-500/20 text-green-400', icon: CheckCircle, label: 'Filed' },
      active: { color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle, label: 'Active' },
      expired: { color: 'bg-orange-500/20 text-orange-400', icon: AlertCircle, label: 'Expired' },
      terminated: { color: 'bg-red-500/20 text-red-400', icon: AlertCircle, label: 'Terminated' }
    };
    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm ${config.color}`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </span>
    );
  };

  return (
    <SubscriptionGate featureName="UCC Filings">
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit'] flex items-center gap-3">
            <Shield className="w-8 h-8 text-blue-400" />
            UCC Orchestration Engine
          </h1>
          <p className="text-slate-400 mt-2">Manage secured interests and collateral filings</p>
        </div>
        <Button
          onClick={() => setActiveTab('create')}
          className="gradient-accent hover:opacity-90"
        >
          <Plus className="w-5 h-5 mr-2" />
          New UCC-1 Filing
        </Button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 text-red-400">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-500/10 border border-green-500/50 rounded-lg p-4 text-green-400">
          {success}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-800">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab('filings')}
            className={`pb-4 px-2 border-b-2 transition-colors ${
              activeTab === 'filings'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
          >
            <FileText className="w-5 h-5 inline mr-2" />
            My Filings
          </button>
          <button
            onClick={() => setActiveTab('create')}
            className={`pb-4 px-2 border-b-2 transition-colors ${
              activeTab === 'create'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
          >
            <Plus className="w-5 h-5 inline mr-2" />
            Create Filing
          </button>
        </div>
      </div>

      {/* Filings Tab */}
      {activeTab === 'filings' && (
        <div className="space-y-4">
          {filings.length === 0 ? (
            <div className="text-center py-16 border border-slate-800 rounded-lg bg-slate-900/50">
              <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No UCC Filings Yet</h3>
              <p className="text-slate-400 mb-6">Create your first UCC-1 filing to secure your interests</p>
              <Button onClick={() => setActiveTab('create')} className="gradient-accent">
                <Plus className="w-5 h-5 mr-2" />
                Create UCC-1 Filing
              </Button>
            </div>
          ) : (
            <div className="grid gap-4">
              {filings.map((filing) => (
                <div
                  key={filing.filing_id}
                  className="border border-slate-800 rounded-lg p-6 bg-slate-900/50 hover:border-slate-700 transition-colors"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold">{filing.filing_number}</h3>
                        {getStatusBadge(filing.status)}
                      </div>
                      <p className="text-slate-400 text-sm">
                        Filing Type: <span className="text-white">{filing.filing_type}</span>
                      </p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-slate-500 uppercase mb-1">Debtor</p>
                      <p className="text-white font-medium">{filing.debtor?.name}</p>
                      <p className="text-slate-400 text-sm">{filing.debtor?.jurisdiction}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase mb-1">Secured Party</p>
                      <p className="text-white font-medium">{filing.secured_party?.name}</p>
                      <p className="text-slate-400 text-sm">{filing.secured_party?.jurisdiction}</p>
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-xs text-slate-500 uppercase mb-1">Collateral</p>
                    <p className="text-slate-300 text-sm">{filing.collateral?.description}</p>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-slate-800">
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-slate-400">
                        <Calendar className="w-4 h-4 inline mr-1" />
                        Created: {new Date(filing.created_at).toLocaleDateString()}
                      </span>
                      {filing.expires_at && (
                        <span className="text-slate-400">
                          Expires: {new Date(filing.expires_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {filing.status === 'draft' && (
                        <Button
                          onClick={() => handleSubmitFiling(filing.filing_id)}
                          size="sm"
                          className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Submit Filing
                        </Button>
                      )}
                      {(filing.status === 'active' || filing.status === 'filed') && (
                        <>
                          <Button
                            onClick={() => handleContinuation(filing.filing_id)}
                            size="sm"
                            className="bg-green-500/20 hover:bg-green-500/30 text-green-400"
                          >
                            Continue (UCC-3)
                          </Button>
                          <Button
                            onClick={() => handleTermination(filing.filing_id)}
                            size="sm"
                            className="bg-orange-500/20 hover:bg-orange-500/30 text-orange-400"
                          >
                            Terminate
                          </Button>
                        </>
                      )}
                      {filing.pdf_url && (
                        <Button
                          onClick={() => window.open(filing.pdf_url, '_blank')}
                          size="sm"
                          variant="outline"
                          className="border-slate-700"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download PDF
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Filing Tab */}
      {activeTab === 'create' && (
        <form onSubmit={handleGenerateUCC} className="space-y-8">
          {/* Debtor Information */}
          <div className="border border-slate-800 rounded-lg p-6 bg-slate-900/50">
            <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-400" />
              Debtor Information
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Debtor Name</Label>
                <Input
                  value={formData.debtor_name}
                  onChange={(e) => handleChange('debtor_name', e.target.value)}
                  placeholder="ABC Corporation"
                  className="bg-slate-900 border-slate-800"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Debtor Type</Label>
                <select
                  value={formData.debtor_type}
                  onChange={(e) => handleChange('debtor_type', e.target.value)}
                  className="w-full h-10 px-3 rounded-md bg-slate-900 border border-slate-800 text-white"
                  required
                >
                  <option value="organization">Organization</option>
                  <option value="individual">Individual</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>Jurisdiction (State)</Label>
                <Input
                  value={formData.debtor_jurisdiction}
                  onChange={(e) => handleChange('debtor_jurisdiction', e.target.value)}
                  placeholder="DE, NY, CA"
                  className="bg-slate-900 border-slate-800"
                  required
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Address</Label>
                <Input
                  value={formData.debtor_address}
                  onChange={(e) => handleChange('debtor_address', e.target.value)}
                  placeholder="1234 Main St, Suite 100, City, State ZIP"
                  className="bg-slate-900 border-slate-800"
                  required
                />
              </div>
            </div>
          </div>

          {/* Secured Party Information */}
          <div className="border border-slate-800 rounded-lg p-6 bg-slate-900/50">
            <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-400" />
              Secured Party Information
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Secured Party Name</Label>
                <Input
                  value={formData.secured_party_name}
                  onChange={(e) => handleChange('secured_party_name', e.target.value)}
                  placeholder="XYZ Bank"
                  className="bg-slate-900 border-slate-800"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Jurisdiction (State)</Label>
                <Input
                  value={formData.secured_party_jurisdiction}
                  onChange={(e) => handleChange('secured_party_jurisdiction', e.target.value)}
                  placeholder="NY"
                  className="bg-slate-900 border-slate-800"
                  required
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Address</Label>
                <Input
                  value={formData.secured_party_address}
                  onChange={(e) => handleChange('secured_party_address', e.target.value)}
                  placeholder="5678 Bank Plaza, City, State ZIP"
                  className="bg-slate-900 border-slate-800"
                  required
                />
              </div>
            </div>
          </div>

          {/* Collateral Information */}
          <div className="border border-slate-800 rounded-lg p-6 bg-slate-900/50">
            <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-yellow-400" />
              Collateral Information
            </h3>
            <div className="space-y-6">
              <div className="space-y-2">
                <Label>Collateral Category</Label>
                <select
                  value={formData.collateral_category}
                  onChange={(e) => handleChange('collateral_category', e.target.value)}
                  className="w-full h-10 px-3 rounded-md bg-slate-900 border border-slate-800 text-white"
                  required
                >
                  {collateralCategories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.label} - {cat.description}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Collateral Description</Label>
                <Textarea
                  value={formData.collateral_description}
                  onChange={(e) => handleChange('collateral_description', e.target.value)}
                  placeholder="All equipment, machinery, and tools used in the business..."
                  className="bg-slate-900 border-slate-800 min-h-[100px]"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Additional Details (Optional)</Label>
                <Textarea
                  value={formData.collateral_details}
                  onChange={(e) => handleChange('collateral_details', e.target.value)}
                  placeholder="Specific serial numbers, makes, models, or other identifying information..."
                  className="bg-slate-900 border-slate-800 min-h-[100px]"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setActiveTab('filings')}
              className="border-slate-700"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="gradient-accent hover:opacity-90 px-8"
            >
              {loading ? (
                <>Processing...</>
              ) : (
                <>
                  <FileText className="w-5 h-5 mr-2" />
                  Generate UCC-1 Filing
                </>
              )}
            </Button>
          </div>
        </form>
      )}
      </div>
    </SubscriptionGate>
  );
}
